import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/pro-regular-svg-icons';

import Authorize, { can } from '../../../components/Authorize';
import { CONFIG } from '../../../components/Authorize/permissions/resources';
import { normalizeCam } from '../../Mapping/Arrange/logic';
import { fetchCamMapJson, getRowDictFile } from '../../Mapping/mapLogic';
import { sortTiles, tileBoxesKey } from '../helpers';
import { useMapView } from '../Map';
import cc from '../style.module.scss';
import {
  EMPTY,
  buildOverlay,
  mergeOverlays,
  roundToSrs,
  seatSquareStyle,
} from './geometry';
import style from './style.module.scss';

const Ctx = createContext(null);
const useSrs = () => useContext(Ctx);

const SRS = ({ scans, children }) => {
  const [show, setShow] = useState(false);
  const [data, setData] = useState(EMPTY);

  // Config-gated: only fetch mapping once an authorized user enables SRS.
  useEffect(() => {
    if (!show || !can(CONFIG) || !scans?.length) {
      setData(EMPTY);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const cams = [...new Set(scans.map((s) => normalizeCam(s?.camera)).filter(Boolean))];
      const [rowDict, ...camEntries] = await Promise.all([
        getRowDictFile().catch(() => null),
        ...cams.map(async (cam) => {
          try {
            return [cam, await fetchCamMapJson(cam)];
          } catch {
            return [cam, null];
          }
        }),
      ]);
      if (cancelled) return;
      const byCam = new Map(camEntries);
      const multi = scans.length > 1;
      setData(mergeOverlays(scans.map((scan) => {
        const tiles = sortTiles(scan.tiles).map((t) => (
          multi ? { ...t, _scanId: String(scan._id) } : t
        ));
        return buildOverlay(byCam.get(normalizeCam(scan.camera)), tiles, scan, rowDict);
      })));
    })().catch(() => { if (!cancelled) setData(EMPTY); });
    return () => { cancelled = true; };
  }, [scans, show]);

  const round = useCallback((detail, setDetail) => {
    if (detail?.kind !== 'detection' || !detail.tile || !detail.bbox
      || !show || !can(CONFIG)) {
      setDetail(detail);
      return;
    }
    const rows = roundToSrs(detail.tile, detail.bbox, data);
    setDetail(rows ? { ...detail, rows: [...rows, ...(detail.rows || [])] } : detail);
  }, [data, show]);

  const api = useMemo(() => ({
    show,
    toggle: () => setShow((v) => !v),
    data,
    round,
  }), [show, data, round]);

  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
};

SRS.Toggle = () => {
  const srs = useSrs();
  if (!srs) return null;
  const label = srs.show ? 'Hide SRS' : 'Show SRS';
  return (
    <Authorize permissions={CONFIG}>
      <div className={cc.field}>
        <span>
          <button
            type="button"
            className={[cc.fieldIconBtn, srs.show && cc.fieldIconBtnOn].filter(Boolean).join(' ')}
            onClick={srs.toggle}
            title={label}
            aria-label={label}
            aria-pressed={srs.show}
          >
            <FontAwesomeIcon icon={srs.show ? faEye : faEyeSlash} />
          </button>
          SRS
        </span>
      </div>
    </Authorize>
  );
};

SRS.Hits = ({ tile, boxKeyPrefix }) => {
  const srs = useSrs();
  const { scale } = useMapView();
  if (!srs?.show) return null;
  const seats = srs.data.byKey[tileBoxesKey(tile, boxKeyPrefix)] || [];
  if (!seats.length) return null;
  const bw = `${1 / Math.max(Number(scale) || 1, 1)}px`;
  return (
    <div className={cc.boxes}>
      {seats.map((s) => (
        <div
          key={s.id}
          title={s.label}
          className={style.hit}
          style={{ ...seatSquareStyle(s), borderWidth: bw }}
        />
      ))}
    </div>
  );
};

SRS.useRound = (setDetail) => {
  const srs = useSrs();
  return useCallback((detail) => (
    srs ? srs.round(detail, setDetail) : setDetail(detail)
  ), [srs, setDetail]);
};

export default SRS;
